// src/client/game/core/EnemySystem.ts

import { Snake } from "./Snake";
import {
  FoodKind,
  type Direction,
  type FoodInstance,
  type GridPos,
} from "./types";
import { EnemyAgent } from "./enemy/EnemyAgent";
import type { EnemyWorldView } from "./enemy/EnemyContext";
import type { EnemyController } from "./enemy/EnemyController";
import { ForageController } from "./enemy/controllers/ForageController";
import { AggroController } from "./enemy/controllers/AggroController";
import { BossController } from "./enemy/controllers/BossController";

/**
 * Minimal view of the world that EnemySystem needs.
 * GameModel satisfies this structurally.
 */
export interface EnemyWorld {
  width: number;
  height: number;
  snake: Snake;
  enemySnakes: Snake[];
  foods: FoodInstance[];
  obstacles: Set<string>;

  /** Called whenever an enemy snake is killed (any cause). */
  onEnemyKilled: () => void;

  /** Optional: record enemy laser beams for rendering/debug. */
  enemyLaserShots?: { origin: GridPos; path: GridPos[] }[];

  /** Optional: apply damage to the player when hit by an enemy laser. */
  onEnemyLaserHitPlayer?: (hitIndex: number) => void;
}

/** Fixed movement interval for all enemy snakes (ms). */
const ENEMY_MOVE_INTERVAL_MS = 160;

/** Hunter enemies can fire an occasional straight laser when lined up. */
const HUNTER_FIRE_COOLDOWN_TICKS = 6;

/** Boss fires more often as it phases up. */
const BOSS_FIRE_COOLDOWN_TICKS_P1 = 7;
const BOSS_FIRE_COOLDOWN_TICKS_P2 = 4;

type EnemyKind = "SCAVENGER" | "HUNTER" | "BOSS";

type EnemyMeta = {
  kind: EnemyKind;
  controller: EnemyController;
  fireCooldownTicks: number;
  bossPhase?: 1 | 2;
};

/**
 * Handles all enemy snake responsibilities:
 * - spawning
 * - delegating movement decisions to an EnemyController
 * - dying on walls/obstacles/tails/player/enemy collisions
 * - consuming food
 *
 * Step 18 wiring:
 * - Introduces a late-game "HUNTER" enemy that uses AggroController (chases player)
 * - Hunters can occasionally fire a straight laser when aligned with the player.
 */
export class EnemySystem {
  private readonly defaultForage = new ForageController();

  /** Parallel metadata for enemySnakes (index-aligned). */
  private meta: EnemyMeta[] = [];

  constructor(
    private readonly world: EnemyWorld,
    private readonly rng: () => number
  ) {}

  /** Called from GameModel.initRunState(). */
  reset(): void {
    // Start each run with zero enemies.
    this.world.enemySnakes.length = 0;
    this.meta = [];
  }

  /** Spawn a single scavenger enemy snake (legacy). */
  spawnEnemySnake(): void {
    this.spawnEnemySnakesInternal(1, "SCAVENGER");
  }

  /** Spawn a single hunter enemy snake (late-game). */
  spawnHunterSnake(): void {
    this.spawnEnemySnakesInternal(1, "HUNTER");
  }

  /** Spawn the level boss snake (late-campaign). */
  spawnBossSnake(): void {
    this.spawnEnemySnakesInternal(1, "BOSS");
  }

  /** Phase hook for the boss (1 = calm, 2 = enraged). */
  setBossPhase(phase: 1 | 2): void {
    for (const meta of this.meta) {
      if (meta.kind !== "BOSS") continue;
      meta.bossPhase = phase;
      const anyCtrl = meta.controller as any;
      if (typeof anyCtrl.setPhase === "function") {
        anyCtrl.setPhase(phase);
      }
      meta.fireCooldownTicks = Math.min(
        meta.fireCooldownTicks,
        phase === 2 ? BOSS_FIRE_COOLDOWN_TICKS_P2 : BOSS_FIRE_COOLDOWN_TICKS_P1
      );
    }
  }

  /** Called from GameModel.tick() after the player moves (if still alive). */
  tick(): void {
    const { width, height, obstacles, snake } = this.world;
    const surviving: Snake[] = [];
    const survivingMeta: EnemyMeta[] = [];
    const originalEnemies = this.world.enemySnakes;

    // Wrap snakes into EnemyAgents for controller strategy.
    const agents: EnemyAgent[] = originalEnemies.map((s, i) => {
      const meta = this.meta[i];
      const controller = meta?.controller ?? this.defaultForage;
      return new EnemyAgent(`enemy-${i}`, s, controller, null);
    });

    const worldView: EnemyWorldView = {
      width,
      height,
      player: snake,
      foods: this.world.foods,
      obstacles,
      enemies: agents,
      isBlocked: (pos: GridPos) => {
        if (pos.x < 0 || pos.x >= width || pos.y < 0 || pos.y >= height) return true;
        return obstacles.has(`${pos.x},${pos.y}`);
      },
    };

    for (let i = 0; i < originalEnemies.length; i++) {
      const enemy = originalEnemies[i];
      const self = agents[i];
      const meta = this.meta[i] ?? {
        kind: "SCAVENGER" as const,
        controller: self.controller,
        fireCooldownTicks: 0,
      };

      const dir = self.controller.decideNextMove({
        world: worldView,
        self,
        rng: this.rng,
        nowMs: 0,
      });

      enemy.setDirection(dir);

      const { newHead, removedTail, hitSelf, hitObstacle } = enemy.step(
        width,
        height,
        obstacles
      );
      void removedTail;

      let enemyDied = hitSelf || hitObstacle;

      // Enemy eats food if it lands on it (just removes it; grows on red).
      if (!enemyDied) {
        this.enemyConsumeFoodAt(enemy, newHead);
      }

      // Step 18: hunters may fire a laser (after they move).
      if (!enemyDied && meta.kind === "HUNTER") {
        this.tryHunterFire(enemy, meta, worldView);
      }

      // Step 19: boss can fire (and phases change its fire rate).
      if (!enemyDied && meta.kind === "BOSS") {
        this.tryBossFire(enemy, meta, worldView);
      }

      // Collide with player snake?
      if (!enemyDied) {
        if (
          snake.segments.some(
            (seg) => seg.x === newHead.x && seg.y === newHead.y
          )
        ) {
          enemyDied = true;
        }
      }

      // Collide with other enemy snakes (original list)
      if (!enemyDied) {
        for (const other of originalEnemies) {
          if (other === enemy) continue;
          if (
            other.segments.some(
              (seg) => seg.x === newHead.x && seg.y === newHead.y
            )
          ) {
            enemyDied = true;
            break;
          }
        }
      }

      // Collide with enemies already processed this tick (survivors so far)
      if (!enemyDied) {
        for (const other of surviving) {
          if (
            other.segments.some(
              (seg) => seg.x === newHead.x && seg.y === newHead.y
            )
          ) {
            enemyDied = true;
            break;
          }
        }
      }

      if (enemyDied) {
        // Notify game model (tracks stat + bonus score).
        this.world.onEnemyKilled();

        // When an enemy dies, its whole body becomes obstacles.
        for (const seg of enemy.segments) {
          obstacles.add(`${seg.x},${seg.y}`);
        }
      } else {
        surviving.push(enemy);
        survivingMeta.push(meta);
      }
    }

    this.world.enemySnakes = surviving;
    this.meta = survivingMeta;
  }

  // ---- Spawning ----

  private spawnEnemySnakesInternal(count: number, kind: EnemyKind): void {
    const { enemySnakes } = this.world;

    for (let i = 0; i < count; i++) {
      const pos = this.findEmptyCell();

      const dirs: Direction[] = [
        { dx: 1, dy: 0 },
        { dx: -1, dy: 0 },
        { dx: 0, dy: 1 },
        { dx: 0, dy: -1 },
      ];
      // Pick any direction that stays in-bounds from the spawn cell.
      const validDirs = dirs.filter((d) => {
        const nx = pos.x + d.dx;
        const ny = pos.y + d.dy;
        return nx >= 0 && nx < this.world.width && ny >= 0 && ny < this.world.height;
      });
      const dir =
        validDirs.length > 0
          ? validDirs[Math.floor(this.rng() * validDirs.length)]
          : { dx: 1, dy: 0 };

      // Enemy snake starts small; boss starts BIG.
      const initialLen = kind === "BOSS" ? 14 : 3;
      const enemy = new Snake(pos, initialLen, dir, ENEMY_MOVE_INTERVAL_MS);
      enemySnakes.push(enemy);

      const controller =
        kind === "BOSS"
          ? new BossController()
          : kind === "HUNTER"
            ? new AggroController()
            : this.defaultForage;

      const bossPhase: 1 | 2 | undefined = kind === "BOSS" ? 1 : undefined;
      const baseCd =
        kind === "BOSS"
          ? BOSS_FIRE_COOLDOWN_TICKS_P1
          : kind === "HUNTER"
            ? HUNTER_FIRE_COOLDOWN_TICKS
            : 0;

      const anyCtrl = controller as any;
      if (bossPhase && typeof anyCtrl.setPhase === "function") {
        anyCtrl.setPhase(bossPhase);
      }

      this.meta.push({
        kind,
        controller,
        bossPhase,
        fireCooldownTicks:
          baseCd > 0 ? Math.max(1, Math.floor(this.rng() * baseCd)) : 0,
      });
    }
  }

  private findEmptyCell(): GridPos {
    const { width, height, snake, enemySnakes, obstacles, foods } = this.world;
    const maxTries = 2000;

    for (let tries = 0; tries < maxTries; tries++) {
      const x = Math.floor(this.rng() * width);
      const y = Math.floor(this.rng() * height);

      const key = `${x},${y}`;
      if (obstacles.has(key)) continue;

      if (snake.segments.some((s) => s.x === x && s.y === y)) continue;

      if (enemySnakes.some((e) => e.segments.some((s) => s.x === x && s.y === y)))
        continue;

      if (foods.some((f) => f.x === x && f.y === y)) continue;

      return { x, y };
    }

    // Fallback: scan
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const key = `${x},${y}`;
        if (obstacles.has(key)) continue;
        if (snake.segments.some((s) => s.x === x && s.y === y)) continue;
        if (enemySnakes.some((e) => e.segments.some((s) => s.x === x && s.y === y)))
          continue;
        if (foods.some((f) => f.x === x && f.y === y)) continue;
        return { x, y };
      }
    }

    // Absolute worst-case (shouldn't happen): just return (0,0)
    return { x: 0, y: 0 };
  }

  // ---- Hunter laser ----

  private tryHunterFire(enemy: Snake, meta: EnemyMeta, worldView: EnemyWorldView): void {
    meta.fireCooldownTicks = Math.max(0, meta.fireCooldownTicks - 1);
    if (meta.fireCooldownTicks > 0) return;

    const head = enemy.segments[0];
    const playerSegs = worldView.player.segments;

    const aim = this.pickLineOfSightAim(head, playerSegs);
    if (!aim) return;

    const { path, hitIndex } = this.buildRayAndHitIndex(worldView, head, aim);

    // Only fire if we can actually hit the player (keeps it fair).
    if (hitIndex < 0 || path.length === 0) return;

    // Record for renderer (optional).
    if (this.world.enemyLaserShots) {
      this.world.enemyLaserShots.push({ origin: head, path });
    }

    // Apply damage to player (optional handler; GameModel provides this).
    this.world.onEnemyLaserHitPlayer?.(hitIndex);

    // Reset cooldown.
    meta.fireCooldownTicks = HUNTER_FIRE_COOLDOWN_TICKS;
  }

  private pickLineOfSightAim(head: GridPos, playerSegs: GridPos[]): Direction | null {
    // Prefer the closest segment in a straight line (row/col).
    let best: { dir: Direction; dist: number } | null = null;

    for (let i = 0; i < playerSegs.length; i++) {
      const p = playerSegs[i];
      if (p.x === head.x) {
        const dy = p.y > head.y ? 1 : -1;
        const dist = Math.abs(p.y - head.y);
        if (!best || dist < best.dist) best = { dir: { dx: 0, dy }, dist };
      } else if (p.y === head.y) {
        const dx = p.x > head.x ? 1 : -1;
        const dist = Math.abs(p.x - head.x);
        if (!best || dist < best.dist) best = { dir: { dx, dy: 0 }, dist };
      }
    }

    return best?.dir ?? null;
  }

  private buildRayAndHitIndex(
    worldView: EnemyWorldView,
    origin: GridPos,
    aim: Direction
  ): { path: GridPos[]; hitIndex: number } {
    const { width, height, obstacles, player } = worldView;

    const path: GridPos[] = [];
    let hitIndex = -1;

    let x = origin.x;
    let y = origin.y;
    const maxSteps = width + height;

    for (let step = 0; step < maxSteps; step++) {
      x += aim.dx;
      y += aim.dy;

      if (x < 0 || x >= width || y < 0 || y >= height) break;

      const p = { x, y };
      path.push(p);

      if (obstacles.has(`${x},${y}`)) break;

      const idx = player.segments.findIndex((s) => s.x === x && s.y === y);
      if (idx !== -1) {
        hitIndex = idx;
        break;
      }
    }

    return { path, hitIndex };
  }

  // ---- Boss laser + phase ----

  private tryBossFire(enemy: Snake, meta: EnemyMeta, worldView: EnemyWorldView): void {
    const phase: 1 | 2 = meta.bossPhase ?? 1;
    const cooldown = phase === 2 ? BOSS_FIRE_COOLDOWN_TICKS_P2 : BOSS_FIRE_COOLDOWN_TICKS_P1;

    meta.fireCooldownTicks = Math.max(0, meta.fireCooldownTicks - 1);
    if (meta.fireCooldownTicks > 0) return;

    const head = enemy.segments[0];
    const playerSegs = worldView.player.segments;

    const aim = this.pickLineOfSightAim(head, playerSegs);
    if (!aim) return;

    const { path, hitIndex } = this.buildRayAndHitIndex(worldView, head, aim);

    // Keep it fair: only fire when it will actually hit the player.
    if (hitIndex < 0 || path.length === 0) return;

    if (this.world.enemyLaserShots) {
      this.world.enemyLaserShots.push({ origin: head, path });
    }

    this.world.onEnemyLaserHitPlayer?.(hitIndex);

    meta.fireCooldownTicks = cooldown;
  }

  // ---- Food consumption ----

  private enemyConsumeFoodAt(enemy: Snake, head: GridPos): void {
    const foods = this.world.foods;
    if (foods.length === 0) return;

    let ate: FoodInstance | null = null;
    const remaining: FoodInstance[] = [];

    for (const f of foods) {
      if (f.x === head.x && f.y === head.y) {
        ate = f;
      } else {
        remaining.push(f);
      }
    }

    if (!ate) return;

    this.world.foods = remaining;

    // Enemy grows only when it eats red food.
    if (ate.id === FoodKind.Normal) {
      enemy.grow(1);
    }
  }
}
